import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SyncEntryCard = ({ sync }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDuration = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const hours = Math.abs(endDate - startDate) / 36e5;
    return `${hours?.toFixed(1)}h`;
  };

  const statusConfig = {
    success: {
      icon: 'CheckCircle2',
      color: 'success',
      label: 'Success',
      bgClass: 'bg-success/10',
      textClass: 'text-success',
      borderClass: 'border-success/20',
      glowClass: 'shadow-[0_0_10px_rgba(34,197,94,0.3)]'
    },
    error: {
      icon: 'XCircle',
      color: 'error',
      label: 'Error',
      bgClass: 'bg-error/10',
      textClass: 'text-error',
      borderClass: 'border-error/20',
      glowClass: 'shadow-[0_0_10px_rgba(239,68,68,0.3)]'
    },
    pending: {
      icon: 'Clock',
      color: 'accent-blue',
      label: 'Pending',
      bgClass: 'bg-accent-blue/10',
      textClass: 'text-accent-blue',
      borderClass: 'border-accent-blue/20',
      glowClass: 'shadow-[0_0_10px_rgba(14,165,233,0.3)]'
    }
  };

  const config = statusConfig?.[sync?.status] || statusConfig?.pending;

  return (
    <div className={`card hover:scale-[1.01] transition-all duration-300 ${isExpanded ? config?.glowClass : ''}`}>
      <div
        className="flex items-start justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <div className={`flex items-center justify-center w-10 h-10 rounded-lg border ${config?.bgClass} ${config?.borderClass}`}>
              <Icon
                name={config?.icon}
                size={20}
                color={`var(--color-${config?.color})`}
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-bold text-foreground">
                  {formatTimestamp(sync?.timestamp)}
                </h3>
                <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${config?.bgClass} ${config?.textClass} ${config?.borderClass} border`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${config?.textClass?.replace('text-', 'bg-')} animate-pulse`}></div>
                  {config?.label}
                </div>
              </div>
              <p className="text-sm text-muted-foreground font-caption">
                {sync?.provider?.charAt(0)?.toUpperCase() + sync?.provider?.slice(1)} Calendar • 
                {sync?.syncFrequency === 'manual' ? 'Manual Sync' : `Auto-Sync (${sync?.syncFrequency})`}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground font-caption">Total Events</p>
              <p className="text-2xl font-bold font-data text-accent-cyan">{sync?.totalEvents}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground font-caption">Flight Events</p>
              <p className="text-2xl font-bold font-data text-accent-blue">{sync?.flightEvents}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground font-caption">Processed</p>
              <p className="text-2xl font-bold font-data text-success">{sync?.successCount}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground font-caption">Errors</p>
              <p className="text-2xl font-bold font-data text-error">{sync?.errorCount}</p>
            </div>
          </div>
        </div>

        <div className="ml-4">
          <Icon
            name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            size={20}
            color="var(--color-muted-foreground)"
          />
        </div>
      </div>
      {isExpanded && (
        <div className="mt-6 pt-6 border-t border-border space-y-4">
          <div>
            <h4 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Plane" size={16} color="var(--color-accent-purple)" />
              Parsed Rotation Details
            </h4>
            {sync?.rotations?.length > 0 ? (
              <div className="space-y-2">
                {sync?.rotations?.map((rotation, idx) => (
                  <div
                    key={idx}
                    className="p-3 rounded-lg bg-card-hover border border-border hover:border-accent-cyan/40 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold font-data text-accent-cyan">
                          {rotation?.rotation_number}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          rotation?.status === 'active' ? 'bg-success/10 text-success border border-success/20' :
                          rotation?.status === 'error'? 'bg-error/10 text-error border border-error/20' : 'bg-accent-blue/10 text-accent-blue border border-accent-blue/20'
                        }`}>
                          {rotation?.status}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground font-caption">
                        {rotation?.total_credit_hours} credit hours
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground font-caption">
                      <span className="flex items-center gap-1">
                        <Icon name="Calendar" size={12} />
                        {new Date(rotation?.start_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                      <span>→</span>
                      <span className="flex items-center gap-1">
                        <Icon name="Calendar" size={12} />
                        {new Date(rotation?.end_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                      <span className="ml-auto">
                        {formatDuration(rotation?.start_date, rotation?.end_date)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 rounded-lg bg-card-hover border border-border text-center">
                <p className="text-sm text-muted-foreground font-caption">
                  No rotations parsed in this sync
                </p>
              </div>
            )}
          </div>

          {sync?.errorCount > 0 && (
            <div>
              <h4 className="text-sm font-bold text-error mb-3 flex items-center gap-2">
                <Icon name="AlertTriangle" size={16} color="var(--color-error)" />
                Parsing Errors
              </h4>
              <div className="p-3 rounded-lg bg-error/5 border border-error/20">
                <p className="text-sm text-muted-foreground font-caption">
                  {sync?.errorCount} event{sync?.errorCount > 1 ? 's' : ''} failed to parse. 
                  Check event format and try manual import.
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              onClick={(e) => {
                e?.stopPropagation();
                // Navigate to rotations list filtered by this sync
              }}
            >
              <Icon name="List" size={14} />
              View Rotations
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncEntryCard;